import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { payments as wiredPayments } from '../api/payments';
import { booking as wiredBooking } from '../api/booking';
import { refusalMessage } from '../api/errors';
import { formatGBP } from '../lib/currency';
import { getCustomerSession } from '../lib/customerSession';
import { ecocashNumber } from '../lib/ecocashNumber';
import { NOT_MOBILE_MONEY, paynowNumber, paynowWallet } from '../lib/paynowNumber';
import { closeCheckout as wiredClose, openCheckout as wiredOpen } from '../lib/openCheckout';
import { stripeFor as wiredStripeFor } from '../lib/stripeCard';
import CardForm from './CardForm';
import './payment.css';

/**
 * A delivery's payment, followed to the end (InGo ADR 0047, 0048 and 0050;
 * the approved checkout, 6 Oct 2026: "Check your phone", "Confirming your
 * payment", "Paid", "Payment didn't go through").
 *
 * Every way to pay stays inside the app (ADR 0050): a card is typed into
 * Stripe's own fields on the card screen; EcoCash and Paynow prompt the
 * client's phone. A gateway's page is opened only for an attempt made on one
 * before that.
 *
 * The server decides: this screen only asks it, every 2 seconds for half a
 * minute, then every 5 (Kangaru's top-up, `useTopUpFlow.ts`), and each ask
 * also makes the server check with the gateway. A delivery is booked for a
 * rider only once paid, so nothing here is final until the server says so.
 */

/** How often to ask: quickly while a payment is being made, then gently. */
export const POLL = { fastMs: 2000, fastForMs: 30000, slowMs: 5000, patienceMs: 180000 };

/** How long "Check your phone" counts down for the PIN (the approved design); the gateway's own wait is the server's. */
export const PROMPT_SECONDS = 90;

/** The server's words for a number EcoCash cannot charge (InGo ADR 0048). */
const NOT_ECOCASH = 'Type the EcoCash number that pays: an Econet number, starting 077 or 078.';

/** The page an attempt made before ADR 0050 opened, as the waiting screen names it (InGo ADR 0047, 0049). */
const PAGE_OF = { card: 'the card page', paynow: 'the payment page' };

/** The full circle of the countdown ring: 2πr for r = 76. */
const RING = 477.5;

/**
 * Each way to pay: the title heads the screen once it is chosen, the button
 * offers it. A chosen way's screen names no payment company (Rio, 12 Oct
 * 2026), so Paynow heads as the mobile money it takes; its button still
 * names it, so a client offered both can tell it from EcoCash's own.
 */
const WAYS = {
  card: { title: 'Card', button: 'Pay by card' },
  mobile: { title: 'Mobile money', button: 'Pay with EcoCash' },
  paynow: { title: 'Mobile money', button: 'Pay with Paynow' },
  cash: { title: 'Cash', button: 'Pay cash on delivery instead' },
};

/** The ways to pay whose phone is prompted, each with the check its number must pass first. */
const PHONE_WAYS = {
  mobile: { valid: ecocashNumber, refusal: NOT_ECOCASH },
  paynow: { valid: paynowNumber, refusal: NOT_MOBILE_MONEY },
};

function money(minor) {
  return formatGBP((Number(minor) || 0) / 100);
}

function Check() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

function Notice() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#c2560f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5" />
      <path d="M12 16.5h.01" />
    </svg>
  );
}

function Phone() {
  return (
    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#c2560f" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
      <path d="M9.5 9.5h5M9.5 12.5h3" />
    </svg>
  );
}

/** The client's own number when a wallet can pay from it: EcoCash's, or Paynow's EcoCash or OneMoney. */
function ownWalletPhone() {
  const phone = getCustomerSession()?.phone || '';

  return paynowNumber(phone) ? phone : '';
}

function Card() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#07408f" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
      <path d="M2.5 10h19M6.5 15h4" />
    </svg>
  );
}

/** Where the payment stands, in the screen's own terms, from the server's answer. */
export function paymentStage(answer) {
  const delivery = answer?.delivery;
  const payment = answer?.payment;
  if (!delivery) return 'loading';
  if (delivery.payment_status === 'paid' || payment?.status === 'paid') return 'paid';
  if (delivery.status === 'cancelled') return 'cancelled';
  // Cash after all: booked for a rider, nothing to pay here.
  if (delivery.status !== 'awaiting_payment') return 'moved_on';
  if (!payment) return 'choose';
  if (payment.status === 'pending') return 'waiting';

  return 'failed';
}

/**
 * Whether the answer is a card still to be typed (ADR 0050): pending, with
 * Stripe's keys, and not yet sent from this screen. Nothing changes on the
 * server until Pay is pressed, so the poll rests meanwhile.
 */
export function cardToType(answer, sentReference = null) {
  const payment = answer?.payment;

  return paymentStage(answer) === 'waiting' && payment?.method === 'card' && Boolean(payment?.stripe?.client_secret) && payment.reference !== sentReference;
}

export default function PaymentPage({
  payments = wiredPayments,
  booking = wiredBooking,
  openCheckout = wiredOpen,
  closeCheckout = wiredClose,
  stripeFor = wiredStripeFor,
  poll = POLL,
  promptSeconds = PROMPT_SECONDS,
}) {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [answer, setAnswer] = useState(null);
  const [problem, setProblem] = useState(state?.problem || '');
  const [busy, setBusy] = useState(false);
  const [choosing, setChoosing] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);
  // Each start, "Check again" and card sent begins a new round of asking.
  const [round, setRound] = useState(0);
  // The card attempt this screen has sent to Stripe: its own word now waits on the server's.
  const [sentCard, setSentCard] = useState(null);
  const sentCardRef = useRef(null);
  // The ways the office has on now: "Pay another way" offers only these.
  const [ways, setWays] = useState([]);
  // The number EcoCash or Paynow prompts (InGo ADR 0048, 0050): the one typed on the price step, else the client's own.
  const [phone, setPhone] = useState(() => state?.phone || ownWalletPhone());
  const started = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());
  const stage = paymentStage(answer);
  const payment = answer?.payment;
  // A phone prompted: EcoCash's, or Paynow's since ADR 0050 (one made on Paynow's page has its page).
  const waitingOnPhone = stage === 'waiting' && (payment?.method === 'mobile' || (payment?.method === 'paynow' && !payment?.redirect_url));
  const typingCard = cardToType(answer, sentCard);

  useEffect(() => {
    let cancelled = false;
    payments.methods().then((list) => {
      if (!cancelled) setWays(list.map((m) => m.key).filter((key) => WAYS[key]));
    });

    return () => {
      cancelled = true;
    };
  }, [payments]);

  const ask = useCallback(async () => {
    try {
      const fresh = await payments.latest(deliveryId);
      setAnswer(fresh);

      return fresh;
    } catch (e) {
      // A dropped connection says nothing about the money: keep asking.
      if (e?.status === 404) setProblem('There is no such delivery.');

      return null;
    }
  }, [payments, deliveryId]);

  // Asks until the money is settled one way or the other, from the start of each round.
  useEffect(() => {
    let timer = null;
    let stopped = false;
    const tick = async () => {
      const fresh = await ask();
      if (stopped) return;
      const now = paymentStage(fresh);
      if (now !== 'waiting' && now !== 'loading') return;
      if (cardToType(fresh, sentCardRef.current)) return;
      const elapsed = Date.now() - started.current;
      if (elapsed > poll.patienceMs) {
        setGaveUp(true);
        return;
      }
      timer = setTimeout(tick, elapsed < poll.fastForMs ? poll.fastMs : poll.slowMs);
    };
    tick();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [ask, poll, round]);

  // "Check your phone" counts down by the second, and only while it is shown.
  useEffect(() => {
    if (!waitingOnPhone) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(tick);
  }, [waitingOnPhone]);

  // Paid: a gateway's page from before ADR 0050, if one is open, is closed.
  useEffect(() => {
    if (stage === 'paid') closeCheckout();
    if (stage === 'moved_on') navigate(`/live-tracking?delivery=${encodeURIComponent(deliveryId)}`, { replace: true });
  }, [stage, closeCheckout, navigate, deliveryId]);

  /** Asks again from now, quickly at first. */
  const askAfresh = () => {
    started.current = Date.now();
    setNow(Date.now());
    setGaveUp(false);
    setRound((n) => n + 1);
  };

  const payWith = async (method) => {
    // A phone is prompted only for a number its wallet answers: said here, before the server is asked.
    const prompted = PHONE_WAYS[method];
    if (prompted && !prompted.valid(phone)) {
      setProblem(prompted.refusal);
      setChoosing(true);

      return;
    }
    setBusy(true);
    setProblem('');
    setChoosing(false);
    try {
      // The number travels only with the ways that prompt it.
      const fresh = prompted ? await payments.start(deliveryId, method, phone) : await payments.start(deliveryId, method);
      setAnswer(fresh);
      askAfresh();
      const url = fresh?.payment?.redirect_url;
      if (url) await openCheckout(url);
    } catch (e) {
      setProblem(refusalMessage(e));
      setChoosing(true);
    } finally {
      setBusy(false);
    }
  };

  // The card screen sent the card: the server's poll says whether it is paid.
  const cardSent = () => {
    sentCardRef.current = payment?.reference || null;
    setSentCard(sentCardRef.current);
    askAfresh();
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await booking.cancel(deliveryId, 'Not paid: cancelled at checkout.');
      await ask();
    } catch (e) {
      setProblem(refusalMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const amount = payment ? money(payment.amount_minor) : '';
  const methodTitle = WAYS[payment?.method]?.title || 'Payment';

  if (stage === 'paid') {
    return (
      <main className="pay-page pay-page--soft" aria-labelledby="pay-title">
        <div className="pay-hero">
          <div className="pay-badge pay-badge--ok">
            <span className="pay-badge__dot">
              <Check />
            </span>
          </div>
          <h1 id="pay-title" className="pay-title">Paid {amount}</h1>
          <p className="pay-lead">Your delivery is booked.</p>
        </div>
        <section className="pay-receipt" aria-label="Receipt">
          {payment?.paid_with ? (
            <div className="pay-receipt__row">
              <span>Paid with</span>
              <span>{payment.paid_with}</span>
            </div>
          ) : null}
          <div className="pay-receipt__row">
            <span>Reference</span>
            <span className="pay-receipt__ref">{payment?.reference}</span>
          </div>
          {payment?.fee_minor > 0 ? (
            <div className="pay-receipt__row">
              <span>Includes a fee of</span>
              <span>{money(payment.fee_minor)}</span>
            </div>
          ) : null}
        </section>
        <div className="pay-status" role="status">
          <strong>Finding you a rider</strong>
          <span>Nearby riders are getting your delivery now.</span>
        </div>
        <div className="pay-actions">
          <Link className="pay-btn pay-btn--primary" to={`/live-tracking?delivery=${encodeURIComponent(deliveryId)}`} replace>
            Track my delivery
          </Link>
        </div>
      </main>
    );
  }

  if (stage === 'cancelled') {
    return (
      <main className="pay-page" aria-labelledby="pay-title">
        <div className="pay-hero">
          <div className="pay-badge pay-badge--warn">
            <Notice />
          </div>
          <h1 id="pay-title" className="pay-title">This delivery was cancelled</h1>
          <p className="pay-lead">It was not paid, and nothing was taken. Book it again whenever you are ready.</p>
        </div>
        <div className="pay-actions">
          <Link className="pay-btn pay-btn--primary" to="/request-delivery" replace>
            Book a delivery
          </Link>
        </div>
      </main>
    );
  }

  if (stage === 'failed' || stage === 'choose' || choosing) {
    // No attempt yet (it could not start), or the client asked: the ways to pay.
    const chooser = stage === 'choose' || choosing;
    const said = problem || (chooser ? '' : payment?.failure || '');
    const numberWords = ways.includes('paynow') ? 'EcoCash or OneMoney number' : 'EcoCash number';

    return (
      <main className="pay-page" aria-labelledby="pay-title">
        <div className="pay-hero">
          {chooser && !said ? null : (
            <div className="pay-badge pay-badge--warn">
              <Notice />
            </div>
          )}
          <h1 id="pay-title" className="pay-title">{chooser ? 'How will you pay?' : 'Payment didn’t go through'}</h1>
          {said ? <p className="pay-lead">{said}</p> : null}
        </div>
        {chooser ? null : (
          <div className="pay-safe">
            <span>No money has left your account. Your delivery is saved and waits for you.</span>
          </div>
        )}
        <div className="pay-actions">
          {chooser && (ways.includes('mobile') || ways.includes('paynow')) ? (
            <div className="pay-field">
              <label className="pay-field__label" htmlFor="pay-eco-phone">
                {numberWords}
              </label>
              <input
                id="pay-eco-phone"
                className="pay-field__input"
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="e.g. 0772 123 456"
                autoComplete="tel"
                maxLength={20}
                disabled={busy}
              />
            </div>
          ) : null}
          {chooser ? (
            ways.map((key, i) => (
              <button key={key} type="button" className={`pay-btn ${i === 0 ? 'pay-btn--primary' : 'pay-btn--outline'}`} onClick={() => payWith(key)} disabled={busy}>
                {WAYS[key].button}
              </button>
            ))
          ) : (
            <>
              <button type="button" className="pay-btn pay-btn--primary" onClick={() => payWith(payment?.method || 'card')} disabled={busy}>
                {{ card: 'Try the card again', mobile: 'Send the request again', paynow: 'Send the request again' }[payment?.method] || 'Try again'}
              </button>
              <button type="button" className="pay-btn pay-btn--outline" onClick={() => setChoosing(true)} disabled={busy}>
                Pay another way
              </button>
            </>
          )}
          <button type="button" className="pay-btn pay-btn--quiet" onClick={cancel} disabled={busy}>
            Cancel this delivery
          </button>
        </div>
      </main>
    );
  }

  if (typingCard) {
    return <CardForm stripe={payment.stripe} amount={amount} stripeFor={stripeFor} onConfirmed={cardSent} onPayAnotherWay={() => setChoosing(true)} />;
  }

  if (waitingOnPhone) {
    // "Check your phone" (the approved design's MobileWait): EcoCash, or Paynow for
    // EcoCash or OneMoney, has prompted the client's phone; the server's answer moves
    // this screen, never the countdown.
    const left = Math.max(0, promptSeconds - Math.floor((now - started.current) / 1000));
    const lapsed = left === 0 || gaveUp;
    const offset = promptSeconds > 0 ? (RING * (1 - left / promptSeconds)).toFixed(1) : String(RING);
    const viaPaynow = payment?.method === 'paynow';
    const wallet = viaPaynow ? paynowWallet(payment?.paying_from) || 'mobile money' : 'EcoCash';

    return (
      <main className="pay-page" aria-labelledby="pay-title">
        <header className="pay-head">
          <span>{methodTitle}</span>
          <span>{amount}</span>
        </header>
        <div className="pay-hero pay-hero--phone">
          <div className="pay-ring" aria-hidden>
            <svg className="pay-ring__track" width="168" height="168" viewBox="0 0 168 168">
              <circle cx="84" cy="84" r="76" fill="none" stroke="#f0f2f5" strokeWidth="8" />
              <circle cx="84" cy="84" r="76" fill="none" stroke="#ec6c23" strokeWidth="8" strokeLinecap="round" strokeDasharray={RING} strokeDashoffset={offset} transform="rotate(-90 84 84)" />
            </svg>
            <div className="pay-ring__face">
              <Phone />
              <span className="pay-ring__time">{`${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`}</span>
            </div>
          </div>
          <h1 id="pay-title" className="pay-title">
            Check your phone
          </h1>
          <p className="pay-lead">
            A request has been sent to <strong>{payment?.paying_from || 'your phone'}</strong>. Enter your {wallet} PIN to pay <strong>{amount}</strong>.
          </p>
          <div className="pay-pill" role="status">
            <span className="pay-pill__dot" aria-hidden />
            <span>{lapsed ? 'Didn’t get it? Send the request again.' : 'Waiting for your PIN'}</span>
          </div>
        </div>
        <ol className="pay-steps">
          <li>
            <span className="pay-steps__n">1</span>
            <span>Unlock your phone</span>
          </li>
          <li>
            <span className="pay-steps__n">2</span>
            <span>{`Open the ${wallet} message on screen`}</span>
          </li>
          <li>
            <span className="pay-steps__n">3</span>
            <span>Enter your PIN. This screen moves on by itself.</span>
          </li>
        </ol>
        <div className="pay-actions">
          <p className="pay-saved">Your delivery is saved. It goes to riders as soon as it is paid.</p>
          {gaveUp ? (
            <button type="button" className="pay-btn pay-btn--outline" onClick={askAfresh} disabled={busy}>
              Check again
            </button>
          ) : null}
          <button type="button" className={`pay-btn ${lapsed ? 'pay-btn--primary' : 'pay-btn--outline'}`} onClick={() => payWith(payment?.method)} disabled={busy}>
            Send the request again
          </button>
          <button type="button" className="pay-btn pay-btn--quiet" onClick={() => setChoosing(true)} disabled={busy}>
            Pay another way
          </button>
        </div>
      </main>
    );
  }

  const page = payment?.redirect_url ? PAGE_OF[payment?.method] : null;

  return (
    <main className="pay-page" aria-labelledby="pay-title">
      <header className="pay-head">
        <span>{methodTitle}</span>
        <span>{amount}</span>
      </header>
      <div className="pay-hero pay-hero--wait">
        <div className="pay-spinner" aria-hidden>
          <Card />
        </div>
        <h1 id="pay-title" className="pay-title">{gaveUp ? 'Still waiting for your bank' : 'Confirming your payment'}</h1>
        <p className="pay-lead" role="status">
          {gaveUp
            ? 'We have not heard yet. If you paid, it will show here as soon as your bank confirms.'
            : page
              ? `Finish paying on ${page}. This screen moves on by itself.`
              : 'This screen moves on by itself.'}
        </p>
      </div>
      <div className="pay-actions">
        {gaveUp ? (
          <button type="button" className="pay-btn pay-btn--primary" onClick={askAfresh} disabled={busy}>
            Check again
          </button>
        ) : null}
        {payment?.redirect_url ? (
          <button type="button" className="pay-btn pay-btn--outline" onClick={() => openCheckout(payment.redirect_url)} disabled={busy}>
            {`Open ${page || 'the payment page'} again`}
          </button>
        ) : null}
        <button type="button" className="pay-btn pay-btn--quiet" onClick={() => setChoosing(true)} disabled={busy}>
          Having trouble? Pay another way
        </button>
      </div>
    </main>
  );
}
